import { Box, Dialog, Typography, useTheme, styled } from '@mui/material'
import config from 'config'
import { Avatar, Divider, Loader, ThresholdSearchInput } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useDebounce } from 'packages/core'
import { useAppState } from 'appContext'
import { useCommonGetAPI } from 'hooks'

const StyledDialog = styled(Dialog, {
    shouldForwardProp: (prop) =>
        prop !== 'topPosition' && prop !== 'rightPosition',
})(({ theme, topPosition, rightPosition }) => ({
    '& .MuiDialog-paper': {
        maxWidth: 'unset',
        position: 'absolute !important',
        left: `${(rightPosition - 16) / 10}rem`,
        top: `${(topPosition - 200) / 10}rem`,
        borderRadius: '0.8rem',
        boxShadow:
            'inset 0 0 0 1px #fbfbfd, 0 0 0 0.4rem rgba(48, 127, 193, 0.05), 0 0.4rem 1.6rem 0.4rem rgba(0, 0, 0, 0.05), 0 0.8rem 3.2rem 0.4rem rgba(0, 0, 0, 0.05)',
    },
}))

const ModalBox = styled(Box)(({ theme }) => ({
    backgroundColor: '#fbfbfd',
    minWidth: '62.7rem',
    minHeight: '38.1rem',
    padding: '22px 24px 23px',
    borderRadius: '0.8rem',
    '&:focus': {
        outline: 'none',
    },
}))

const ModalHeader = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '2rem',
}))

const Title = styled(Typography)(({ theme }) => ({
    fontSize: '1.6rem',
    fontWeight: 'bold',
    color: theme?.palette.common.black,
}))

const SearchResults = styled(Box)(({ theme }) => ({
    width: '579px',
    height: '224px',
    margin: '1.6rem 0 0',
    padding: '9.5px 7px 6px 6px',
    borderRadius: '5px',
    boxShadow: '0 4px 6px 0 rgba(0, 0, 0, 0.07)',
    border: 'solid 1px #d4d4d8',
    backgroundColor: theme?.palette.common.white,
    overflow: 'auto',
}))

const PersonOptionBox = styled(Box)(({ theme }) => ({
    cursor: 'pointer',
    marginBottom: '3px',
    '& div': {
        color: '#73767A',
    },
    '&:hover, &:focus-visible': {
        backgroundColor: '#f5f6f8',
        '& div .friendly-name-text': {
            color: theme?.palette.primary.main,
        },
        '& div .login-text': {
            color: '#3b454d',
        },
    },
}))

const LineGlow = styled('div')(() => ({
    height: '1px',
    opacity: '0.25',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderImageSource:
        'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab93 7%, #2c83bd 91%, rgba(48, 127, 193, 0))',
    borderImageSlice: '1',
}))

const PersonOption = ({
    option,
    labelProp = 'friendlyName',
    emailProp = 'email',
    imageUrlProp = 'imageThumbUrl',
    cdnUrl,
    disableImage = false,
    onSelect,
    tabIndex = 0,
    isSelected = false,
    optionRef,
}) => {
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect(e, option)
        }
    }

    return (
        <PersonOptionBox
            display="flex"
            alignItems="center"
            padding="0.8rem"
            tabIndex={tabIndex}
            onKeyDown={handleKeyDown}
            onClick={(e) => onSelect(e, option)}
            role="option"
            aria-selected={isSelected}
            ref={optionRef}
        >
            {!disableImage && (
                <Box component="span" paddingRight="2rem">
                    <Avatar src={`${cdnUrl}${option[imageUrlProp]}`} />
                </Box>
            )}
            <Box style={{ display: 'flex' }}>
                <Typography
                    display="block"
                    className="friendly-name-text"
                    style={{
                        fontSize: '1.6rem',
                        fontWeight: 'normal',
                        width: '22rem',
                    }}
                    noWrap={true}
                >
                    {option[labelProp]}
                </Typography>
                <Typography
                    display="block"
                    style={{
                        fontSize: '1.2rem',
                        width: '25.6rem',
                    }}
                    noWrap={true}
                    className="login-text"
                    title={option[emailProp]}
                >
                    {option[emailProp]}
                </Typography>
            </Box>
        </PersonOptionBox>
    )
}
const searchFields = [
    {
        name: 'First Name',
        placeholder: 'Common_FirstName',
        queryParam: 'FirstName',
    },
    {
        name: 'Last Name',
        placeholder: 'Common_LastName',
        queryParam: 'LastName',
    },
    {
        name: 'Email',
        placeholder: 'Common_Email',
        queryParam: 'Email',
    },
    {
        name: 'Login',
        placeholder: 'Common_Login',
        queryParam: 'Login',
        sxNotify: {
            right: 0,
        },
    },
]
export const AdvancePeopleFinder = (props) => {
    const {
        open,
        onClose,
        onChange,
        dedupingInterval = 2000,
        debounceInterval = 300,
        url,
        topPosition,
        rightPosition,
    } = props
    const theme = useTheme()
    const [{ headerHeight, subHeaderHeight }] = useAppState()
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const optionRefs = useRef([])
    const searchResultsRef = useRef(null)

    const initialValues = searchFields.reduce((acc, field) => {
        acc[field.queryParam] = '' // Set each queryParam as a key with an empty string value
        return acc
    }, {})
    const inputStyles = {
        '& MuiInputBase-root': {
            height: '3.2rem',
            marginTop: theme.spacing(3),
        },
        input: {
            borderRadius: '0.5rem',
            position: 'relative',
            backgroundColor: `${theme?.palette?.background?.paper} !important`,
            marginBottom: '1.6rem !important',
            border: '0.1rem solid #d8d8dd',
            fontSize: '1.4rem',
            padding: '0.5rem 0.8rem 0.5rem 1.2rem',
            width: '11rem',
            lineHeight: 1.25,
            boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
            '&:hover, &:focus': {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.common.white,
            },
        },
        '&.focused': {
            backgroundColor: theme.palette.common.white,
        },
        error: {
            '& input, & textarea': {
                border: '0.1rem solid #f44336 !important',
            },
        },
    }

    const [searchTerms, setSearchTerms] = useState(initialValues)
    const [callEndpoint, setCallEndpoint] = useState(false)
    const debouncedValue = useDebounce(searchTerms, debounceInterval)

    const checkIfAnyValueExists = (obj) => {
        for (const key in obj) {
            if (obj[key] !== '') {
                return true // If any key has a non-empty value, return true
            }
        }
        return false // If all keys are empty, return false
    }
    const queryParamNames = []
    const queryParamValues = []
    const queryParamTypes = []
    const queryStringParts = []
    if (checkIfAnyValueExists(debouncedValue)) {
        for (const key in debouncedValue) {
            if (debouncedValue[key] !== '') {
                queryParamNames.push(key)
                queryParamValues.push(debouncedValue[key].replace(',', ' '))
                queryParamTypes.push(typeof debouncedValue[key])
            }
        }
    }

    if (
        queryParamNames.length > 0 &&
        queryParamValues.length === queryParamNames.length
    ) {
        queryStringParts.push(
            `advancedSearchParamNames=${queryParamNames.join(',')}`,
        )
        queryStringParts.push(
            `advancedSearchParamValues=${queryParamValues.join(',')}`,
        )
        queryStringParts.push(
            `advancedSearchParamTypes=${queryParamTypes.join(',')}`,
        )
    }
    const queryString = queryStringParts.join('&')

    const urlWithQs = queryString ? `${url}?${queryString}` : url

    const { data, isFetching: isValidating } = useCommonGetAPI(
        callEndpoint && open ? urlWithQs : undefined,
    )

    useEffect(() => {
        setCallEndpoint(true)
    }, [debouncedValue])
    const getModalTopPosition = () => {
        const topHeadersHeight = subHeaderHeight + headerHeight
        const midOfInputFromTop = topPosition + 25
        const halfOfPopupHeight = (23.18 * 16) / 2
        if (halfOfPopupHeight <= midOfInputFromTop - topHeadersHeight) {
            return midOfInputFromTop
        } else {
            const diff =
                midOfInputFromTop - topHeadersHeight - halfOfPopupHeight
            return midOfInputFromTop + Math.abs(diff) - 25
        }
    }
    const { t } = useTranslation()

    const onClosePopup = useCallback(() => {
        setCallEndpoint(false)
        setSearchTerms(initialValues)
        setSelectedIndex(-1)
        onClose()
    }, [onClose, initialValues])

    // Reset selected index when data changes
    useEffect(() => {
        setSelectedIndex(-1)
        optionRefs.current = []
    }, [data])

    // Focus selected item when index changes
    useEffect(() => {
        if (selectedIndex >= 0 && optionRefs.current[selectedIndex]) {
            optionRefs.current[selectedIndex].focus()
        }
    }, [selectedIndex])

    const handleKeyDown = useCallback(
        (e) => {
            const results = data?.data || []
            const maxIndex = results.length - 1

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    setSelectedIndex((prev) => {
                        if (prev < maxIndex) return prev + 1
                        return 0 // Wrap to first item
                    })
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    setSelectedIndex((prev) => {
                        if (prev > 0) return prev - 1
                        if (prev === -1 || prev === 0) return maxIndex // Wrap to last item
                        return prev
                    })
                    break
                case 'Escape':
                    e.preventDefault()
                    onClosePopup()
                    break
                default:
                    break
            }
        },
        [data, onClosePopup],
    )
    return (
        <>
            <StyledDialog
                scroll={'body'}
                open={open}
                onClose={onClose}
                topPosition={getModalTopPosition()}
                rightPosition={rightPosition}
                BackdropProps={{ style: { opacity: '0.5' } }}
            >
                <ModalBox>
                    <ModalHeader>
                        <Title>{t('AdvancedPersonFinder')}</Title>
                        {/* Commenting below close icon as it is not in zeplin design but maybe required in future.
                        Code will be removed if there's no requirement in near future */}
                        {/* <CloseIconBox>
                            <IconButton onClick={() => onClosePopup()}>
                                <Icon name="Close" color="#959598" />
                            </IconButton>
                        </CloseIconBox> */}
                    </ModalHeader>

                    <Box
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap', // Ensures items wrap to the next line when space is limited
                            gap: '17px', // Adjust the gap between input fields
                            marginTop: '23px',
                        }}
                    >
                        {searchFields.map((field, i) => {
                            return (
                                <>
                                    <Box
                                        style={{
                                            display: 'flex',
                                            position: 'relative',
                                        }}
                                        key={i + field.queryParam}
                                    >
                                        <ThresholdSearchInput
                                            placeholder={t(field.placeholder)}
                                            handleSearch={(value) =>
                                                setSearchTerms((pre) => ({
                                                    ...pre,
                                                    [field.queryParam]: value,
                                                }))
                                            }
                                            sx={inputStyles}
                                            sxNotify={{
                                                position: 'absolute',
                                                top: '-3.2rem',
                                                ...(field.sxNotify || {}),
                                            }}
                                        />
                                    </Box>
                                </>
                            )
                        })}
                    </Box>
                    <Divider color="#d8d8d8" />
                    <SearchResults
                        ref={searchResultsRef}
                        onKeyDown={handleKeyDown}
                        role="listbox"
                        aria-label={t('AdvancedPersonFinder')}
                        tabIndex={0}
                    >
                        {isValidating && <Loader />}
                        {data?.data?.length === 0 && !isValidating ? (
                            <Box
                                style={{
                                    margin: '0px 0px',
                                    justifyContent: 'center',
                                    display: 'flex',
                                }}
                            >
                                {t('Common_NoDataFound')}
                            </Box>
                        ) : (
                            !isValidating &&
                            data?.data?.map((t, i) => {
                                return (
                                    <PersonOption
                                        key={t.personGUID || i}
                                        option={t}
                                        cdnUrl={config.BASE_EID_URL}
                                        onSelect={(_, value) => {
                                            onChange(_, value)
                                            onClosePopup()
                                        }}
                                        tabIndex={selectedIndex === i ? 0 : -1}
                                        isSelected={selectedIndex === i}
                                        optionRef={(el) =>
                                            (optionRefs.current[i] = el)
                                        }
                                    />
                                )
                            })
                        )}
                    </SearchResults>
                </ModalBox>
                <LineGlow />
            </StyledDialog>
        </>
    )
}
